import {
  PermissionCode,
  type MaintenanceWorkOrderListQuery,
  type MaintenanceWorkOrderStatus,
} from '@gb-ticket/shared-types';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MaintenanceOptionalSelect } from '@/products/maintenance/features/maintenance-filter-select';
import {
  maintenanceHomeOrdersPath,
  maintenanceHomeOrdersQueryFromSearch,
} from '@/products/maintenance/features/maintenance-home-path';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import {
  maintenanceSearchFromPath,
  nextMaintenanceColumnSort,
} from '@/products/maintenance/features/maintenance-list-query';
import {
  maintenanceWorkOrderCustomerColumn,
  maintenanceWorkOrderDueColumn,
  maintenanceWorkOrderInstallationColumn,
  maintenanceWorkOrderScheduledColumn,
} from '@/products/maintenance/features/maintenance-work-order-list-columns';
import {
  isMaintenanceWorkOrderSortBy,
  MAINTENANCE_HOME_SORT_FIELDS,
  useRememberedWorkOrderSort,
  writeMaintenanceWorkOrderListSort,
} from '@/products/maintenance/features/maintenance-work-order-list-sort';
import { MaintenanceListToolbar } from '@/products/maintenance/features/maintenance-list-toolbar';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import { useMaintenanceRowSelection } from '@/products/maintenance/features/maintenance-row-selection';
import { MAINTENANCE_WORK_ORDER_STATUS_OPTIONS } from '@/products/maintenance/features/maintenance-status';
import {
  MaintenanceWorkOrderArchiveBar,
  MaintenanceWorkOrderArchivedBadge,
} from '@/products/maintenance/features/work-orders/maintenance-work-order-archive-actions';
import { useMyMaintenanceWorkOrders } from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import {
  MAINTENANCE_CUSTOMERS_PATH,
  MAINTENANCE_PLANNING_PATH,
  MAINTENANCE_TEMPLATES_PATH,
  maintenanceWorkOrderPath,
} from '@/products/maintenance/maintenance-paths';

export function MaintenanceHomePage() {
  usePageTitle('Wartungsprotokolle');
  const me = useMe();
  const [search, setSearch] = useSearchParams();
  const parsedQuery = useMemo(
    () => maintenanceHomeOrdersQueryFromSearch(search.toString()),
    [search],
  );
  const listQuery = useRememberedWorkOrderSort(
    search.toString(),
    'home',
    MAINTENANCE_HOME_SORT_FIELDS,
    parsedQuery,
  );
  const masterData = hasPermission(me.data, PermissionCode.MAINTENANCE_MASTER_DATA_MANAGE);
  const templates = hasPermission(me.data, PermissionCode.MAINTENANCE_TEMPLATES_MANAGE);
  const manageOrders = hasPermission(me.data, PermissionCode.MAINTENANCE_ORDERS_MANAGE);
  const readOwn = hasPermission(me.data, PermissionCode.MAINTENANCE_ORDERS_READ_OWN);
  const mine = useMyMaintenanceWorkOrders(listQuery);
  const items = useMemo(() => mine.data?.items ?? [], [mine.data]);
  const visibleIds = useMemo(() => items.map((order) => order.id), [items]);
  const selection = useMaintenanceRowSelection(visibleIds);
  const extraCount = [listQuery.scheduledFrom, listQuery.scheduledTo].filter(Boolean).length;
  const filtered = Boolean(listQuery.status) || listQuery.includeArchived || extraCount > 0;

  function apply(patch: Partial<MaintenanceWorkOrderListQuery>) {
    setSearch(
      maintenanceSearchFromPath(
        maintenanceHomeOrdersPath({ ...listQuery, ...patch, page: patch.page ?? 1 }),
      ),
    );
  }

  function resetFilters() {
    setSearch(
      maintenanceSearchFromPath(
        maintenanceHomeOrdersPath({
          page: 1,
          pageSize: listQuery.pageSize,
          sortBy: listQuery.sortBy,
          sortOrder: listQuery.sortOrder,
        }),
      ),
    );
  }

  function onColumnSort(sortBy: string) {
    if (!isMaintenanceWorkOrderSortBy(sortBy, MAINTENANCE_HOME_SORT_FIELDS)) {
      return;
    }
    const next = nextMaintenanceColumnSort(listQuery, sortBy);
    writeMaintenanceWorkOrderListSort('home', { sortBy, sortOrder: next.sortOrder });
    apply(next);
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Wartungsprotokolle"
        description="Digitale Wartungsprotokolle und Checklisten für Kundendienst und Monteure."
      />
      {masterData || templates || manageOrders ? (
        <p className="flex flex-col gap-1 text-sm">
          {manageOrders ? (
            <Link className="text-primary hover:underline" to={MAINTENANCE_PLANNING_PATH}>
              Aufträge planen
            </Link>
          ) : null}
          {masterData ? (
            <Link className="text-primary hover:underline" to={MAINTENANCE_CUSTOMERS_PATH}>
              Kunden und Stammdaten verwalten
            </Link>
          ) : null}
          {templates ? (
            <Link className="text-primary hover:underline" to={MAINTENANCE_TEMPLATES_PATH}>
              Wartungsvorlagen verwalten
            </Link>
          ) : null}
        </p>
      ) : null}

      {readOwn ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Meine Aufträge</h2>
          <MaintenanceListToolbar
            extraCount={extraCount}
            filtered={filtered}
            onReset={resetFilters}
            extra={
              <>
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="home-from">Termin von</Label>
                  <Input
                    id="home-from"
                    type="date"
                    className="min-h-11"
                    value={listQuery.scheduledFrom ?? ''}
                    onChange={(event) => apply({ scheduledFrom: event.target.value || undefined })}
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label htmlFor="home-to">Termin bis</Label>
                  <Input
                    id="home-to"
                    type="date"
                    className="min-h-11"
                    value={listQuery.scheduledTo ?? ''}
                    onChange={(event) => apply({ scheduledTo: event.target.value || undefined })}
                  />
                </div>
              </>
            }
          >
            <MaintenanceOptionalSelect
              label="Status"
              id="home-status"
              value={listQuery.status ?? ''}
              onValueChange={(status) =>
                apply({
                  status: status ? (status as MaintenanceWorkOrderStatus) : undefined,
                })
              }
              items={MAINTENANCE_WORK_ORDER_STATUS_OPTIONS}
            />
            <div className="flex min-h-11 items-center gap-2">
              <Checkbox
                id="home-archived"
                checked={listQuery.includeArchived}
                onCheckedChange={(value) => apply({ includeArchived: value === true })}
              />
              <Label htmlFor="home-archived">Archivierte anzeigen</Label>
            </div>
          </MaintenanceListToolbar>
          <MaintenanceWorkOrderArchiveBar
            orders={items}
            selectedIds={selection.selectedIds}
            onClear={() => selection.clear()}
          />
          <MaintenanceRecordList
            rows={items}
            pending={mine.isPending ? 'Aufträge werden geladen …' : undefined}
            emptyLabel="Keine zugewiesenen Aufträge."
            filtered={filtered}
            onReset={resetFilters}
            getRowId={(order) => order.id}
            getHref={(order) => maintenanceWorkOrderPath(order.id)}
            getTitle={(order) => order.orderNumber}
            getMeta={(order) => `${order.customer.name} · ${order.installation.name}`}
            getStatus={(order) => order.status}
            renderTitleExtra={
              listQuery.includeArchived
                ? (order) => <MaintenanceWorkOrderArchivedBadge order={order} />
                : undefined
            }
            isRowArchived={
              listQuery.includeArchived ? (order) => order.archivedAt != null : undefined
            }
            selection={selection}
            titleHeader="Auftrag"
            titleSortKey="orderNumber"
            statusSortKey="status"
            sort={{
              sortBy: listQuery.sortBy,
              sortOrder: listQuery.sortOrder,
              onSort: onColumnSort,
            }}
            columns={[
              maintenanceWorkOrderCustomerColumn(),
              maintenanceWorkOrderInstallationColumn(),
              maintenanceWorkOrderScheduledColumn(),
              maintenanceWorkOrderDueColumn(),
            ]}
          />
          {items.length > 0 && mine.data ? (
            <MaintenanceListFooter
              page={mine.data.page}
              pageSize={mine.data.pageSize}
              total={mine.data.total}
              onPageChange={(page) => apply({ page })}
            />
          ) : null}
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">
          Aufträge und Protokolle stehen Monteur-Rollen zur Verfügung, sobald sie freigeschaltet
          sind.
        </p>
      )}
    </div>
  );
}
